<template>
  <div class="dash-view dash-create pb-0">
    <div class="chart-wrap">
      <div v-for="(chart, index) in droppedCharts" :key="index" class="chart-column drag-drop-zone"
        @dragover.prevent="setDragging(index, true)" @dragleave.prevent="setDragging(index, false)"
        @drop.prevent="handleDrop($event, index)" draggable="true" @dragstart="startDrag($event, chart, index)"
        :class="{ 'is-dragging': draggingStates[index] }">
        <!-- <div class="dash-chart" v-if="loading[index]"> -->
        <DashboardCharts v-if="loading[index]" />
        <!-- </div> -->
        <p v-if="!chart">Drag and Drop your desired plots here</p>
        <div v-else>
          <div v-if="!loading[index]">
            <div v-if="noData[index]" class="tabs-no-test-div">
              <h6>{{ noData[index] }}</h6>
            </div>
            <div v-else>
              <h6 class="chart-name-top" v-if="droppedCharts[index]?.Name">
                {{ droppedCharts[index].Name }}
              </h6>
              <div :ref="'chart_' + index" class="chart-main"></div>
            </div>
          </div>
        </div>
        <v-dialog
          v-model="isPreviewOpen[index]"
          class="multi-chart-preview-div dashboard-chart-preview composition qaqc-test-multi-chart-preview-div"
        >
          <v-card class="studio-card">
            <v-card-text class="qaqc-test-modal-card-text">
              <div class="popup-top">
                <div
                  v-if="chart?.ChartType === 'Scatter Plot' || chart?.ChartType === 'Line Plot'"
                  class="modal-tab-div"
                >
                  <div class="tab-div justify-center">
                    <p>X Axis Scale</p>
                    <v-btn-toggle v-model="isModalXLog" class="axis-toggle" mandatory @update:modelValue="handleModalXScaleToggleChange">
                      <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                      <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                    </v-btn-toggle>
                  </div>
                  <div class="tab-div justify-center">
                    <p>Y Axis Scale</p>
                    <v-btn-toggle v-model="isModalYLog" class="axis-toggle" mandatory @update:modelValue="handleModalYScaleToggleChange">
                      <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                      <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                    </v-btn-toggle>
                  </div>
                </div>
                <div class="chart-name-top w-100">
                  <h6>{{ chart?.Name }}</h6>
                </div>
              </div>
              <div :ref="'modal_chart_' + index" class="modal-chart-container"></div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </div>
    <v-snackbar v-model="showAlert" color="#E53535" timeout="5000">
      Error : {{ errorMessage }}
    </v-snackbar>
    <v-snackbar v-model="showSuccess" color="#4CAF50" timeout="5000">
      Success : {{ successMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import api from "../../../axiosInterceptor";
import { getDisplayNameDashboard } from "../../../utils/cache";
import DashboardCharts from "../Loaders/DashboardCharts.vue";
import Expand from "../../../assets/Svg/Chart/expand-btn.svg";
import downloadIcon from "../../../assets/Svg/Chart/csv_chart.svg";
import { eventBus } from "../../../eventBus.js";
import { mapGetters } from "vuex";
import { formatNumber, shouldFormatNumbers, createPlotlyTickFormat, createCustomTickFormatter } from "../../../utils/numberFormatter";
import { buildFilterQueryParams } from "@/utils/plotlyMiniTransform";
import { attachPlotlyLegendWheelScroll } from "@/utils/plotlyLegendScroll";
import { getTenantPrefix } from "@/utils/tenant";
import { chartGridColor } from "@/utils/chartTheme";

export default {
  name: "DashboardView",
  components: {
    DashboardCharts,
  },
  data() {
    return {
      duplicate: require("../../../assets/Svg/Dashboard/duplicate.svg"),
      cross: require("../../../assets/Svg/Chart/chart-cross.svg"),
      camIcon: require("../../../assets/Svg/camIcon.svg"),
      draggingStates: {},
      droppedCharts: Array(6).fill(null), // Max 6 charts
      previousDroppedCharts: Array(6).fill(null), 
      tableData: [],
      selectedColorGroupKeys: [],
      selectedYAxisKeys: [],
      selectedXAxisKeys: [],
      selectedZAxisKeys: [],
      updatedXColumnDisplayName: [],
      updatedYColumnDisplayName: [],
      updatedGroupColumnDisplayName: [],
      isPreviewOpen: [],
      loading: [],
      noData: [],
      dataLoaded: [],
      xBins: [],
      yBins: [],
      markerSizes: [],
      xAxisScaleModal: "linear",
      yAxisScaleModal: "linear",
      isModalXLog: false,
      isModalYLog: false,
      dashboard_id: "",
      showAlert: false,
      showSuccess: false,
      errorMessage: "",
      successMessage: "",
      expandedIndex: null,
      chartPlotCache: {},
      legendScrollCleanups: {},
    };
  },
  props: {
    name: {
      type: String,
      default: "untitled",
    },
    accessLevel: {
      type: String,
      default: "private",
    },
    isDefault: {
      type: Boolean,
      default: false,
    },
    charts: {
      type: Array,
      default: () => [],
    },
    dashboardID: {
      type: String,
    },
    isEditing: {
      type: Boolean,
      required: true
    }
  },
  watch: {
  charts: {
    immediate: true,
    async handler(newValue) {
      const chartsList = Array.isArray(newValue) ? newValue : [];
      this.previousDroppedCharts = [...this.droppedCharts];
      this.droppedCharts = Array(6).fill(null);

      for (let index = 0; index < 6; index++) {
        if (index < chartsList.length && chartsList[index]) {
          this.droppedCharts[index] = chartsList[index];
          await this.fetchChartsData(chartsList[index], index);
        } else {
          this.droppedCharts[index] = null;
          this.renderChart(null, index, "chart_");
        }
      }

      const chartIds = chartsList.filter((c) => c && c.ID).map((c) => c.ID);
      if (chartIds.length > 0) {
        eventBus.emit("chart-IDs", chartIds);
      }
    },
  },
  name: function (newValue, oldValue) {
    if (newValue !== oldValue) {
      if (!this.droppedCharts.every(chart => chart === null)) {
        this.updateDashboard();
      }
    }
  },
  accessLevel: function () {
    if (!this.droppedCharts.every(chart => chart === null)) {
      this.updateDashboard();
    }
  },
  isDefault: function () {
    if (!this.droppedCharts.every(chart => chart === null)) {
      this.updateDashboard();
    }
  },
  dashboardID: function (newValue) {
    this.dashboard_id = newValue;
  },
  isDarkMode() {
    this.reloadAllCharts();
  },
  xAxisScaleModal() {
    if (this.expandedIndex != null && this.isPreviewOpen[this.expandedIndex]) {
      const chart = this.droppedCharts[this.expandedIndex];
      if (chart) {
        this.plotModalChart(chart, this.expandedIndex);
      }
    }
  },
  yAxisScaleModal() {
    if (this.expandedIndex != null && this.isPreviewOpen[this.expandedIndex]) {
      const chart = this.droppedCharts[this.expandedIndex];
      if (chart) {
        this.plotModalChart(chart, this.expandedIndex);
      }
    }
  },
},

  methods: {
    getColorForIndex(index) {
      const colors = [
        "rgba(0, 100, 250, 0.8)",   // Blue
        "rgba(255, 99, 71, 0.8)",   // Red
        "rgba(50, 205, 50, 0.8)",   // Green
        "rgba(255, 215, 0, 0.8)",   // Gold
        "rgba(147, 112, 219, 0.8)", // Purple
        "rgba(255, 165, 0, 0.8)"    // Orange
      ];
      return colors[index % colors.length];
    },

    calculateChartHeight() {
      const screenHeight = window.innerHeight;
      const screenWidth = window.innerWidth;
      const zoomLevel = window.devicePixelRatio || 1;
      let baseHeight = 500; // Default
      
      if (screenHeight >= 1080) {
        baseHeight = Math.min(screenHeight * 0.5, 700); 
      } else if (screenHeight >= 768) {
        baseHeight = Math.min(screenHeight * 0.45, 550); 
      } else {
        baseHeight = Math.min(screenHeight * 0.4, 450); 
      }
      
      if (zoomLevel > 1) {
        baseHeight = baseHeight * 0.9; 
      }
      
      return Math.max(baseHeight, 300); 
    },
    calculateModalChartHeight() {
      const screenHeight = window.innerHeight;
      const screenWidth = window.innerWidth;
      const zoomLevel = window.devicePixelRatio || 1;
      let baseHeight = 600;
      
      if (screenHeight >= 1080) {
        baseHeight = Math.min(screenHeight * 0.6, 800); 
      } else if (screenHeight >= 768) {
        baseHeight = Math.min(screenHeight * 0.55, 650); 
      } else {
        baseHeight = Math.min(screenHeight * 0.5, 550); 
      }
      
      if (zoomLevel > 1) {
        baseHeight = baseHeight * 0.9; 
      }
      
      return Math.max(baseHeight, 380); 
    },

    handleResize() {
      this.droppedCharts.forEach((chart, index) => {
        if (chart && this.dataLoaded && this.dataLoaded[index]) {
          this.renderChart(chart, index, "chart_");
        }
      });
    },

  resetDashboardState() {
    this.draggingStates = {};
    this.previousDroppedCharts = Array(6).fill(null);
    this.droppedCharts = Array(6).fill(null);
    this.tableData = [];
    this.selectedColorGroupKeys = [];
    this.selectedYAxisKeys = [];
    this.selectedXAxisKeys = [];
    this.selectedZAxisKeys = [];
    this.updatedXColumnDisplayName = [];
    this.updatedYColumnDisplayName = [];
    this.updatedGroupColumnDisplayName = [];
    this.isPreviewOpen = [];
    this.loading = [];
    this.xBins = [];
    this.yBins = [];
    this.xAxisScaleModal = "linear";
    this.yAxisScaleModal = "linear";
    this.isModalXLog = false;
    this.isModalYLog = false;
    this.dashboard_id = "";
    this.showAlert = false;
    this.showSuccess = false;
  },

  handleModalXScaleToggleChange() {
    this.xAxisScaleModal = this.isModalXLog ? "log" : "linear";
  },

  handleModalYScaleToggleChange() {
    this.yAxisScaleModal = this.isModalYLog ? "log" : "linear";
  },

  reloadAllCharts() {
    this.droppedCharts.forEach((chart, index) => {
      if (chart && this.dataLoaded[index]) {
        this.renderChart(chart, index, "chart_");
      }
    });
  },

  setDragging(index, isDragging) {
    this.draggingStates[index] = isDragging;
  },

  startDrag(event, chart, oldIndex = null) {
    event.dataTransfer.setData("chart", JSON.stringify(chart));
    event.dataTransfer.setData("oldIndex", oldIndex); // Save old position if dragging a dropped chart
  },

  async handleDrop(event, newIndex) {
  this.setDragging(newIndex, false);

  try {
    const chartData = event.dataTransfer.getData("chart");
    const oldIndex = event.dataTransfer.getData("oldIndex");
    const chart = JSON.parse(chartData);

    if (!chart || !chart.Name) return;

    this.previousDroppedCharts = JSON.parse(JSON.stringify(this.droppedCharts));

    if (oldIndex !== "null" && oldIndex !== "" && oldIndex !== null) {
      const movedChart = this.previousDroppedCharts[newIndex];

      if (movedChart && movedChart.Name) {
        this.droppedCharts[oldIndex] = null;
        this.$nextTick(() => {
          this.droppedCharts[oldIndex] = movedChart;
          this.droppedCharts[oldIndex].Name = movedChart.Name; // ✅ Name ensure karo

          this.droppedCharts[newIndex] = chart;
          this.droppedCharts[newIndex].Name = chart.Name; // ✅ Name ensure karo

          this.fetchChartsData(movedChart, oldIndex);
          this.fetchChartsData(chart, newIndex);
          this.updateDashboard(chart);
        });
      }
    } else {
      if (this.droppedCharts[newIndex]) {
        const availableIndex = this.previousDroppedCharts.findIndex(c => c === null);
        if (availableIndex !== -1) {
          this.droppedCharts[availableIndex] = this.previousDroppedCharts[newIndex];
          this.$nextTick(() => {
            this.droppedCharts[newIndex] = chart;
            this.droppedCharts[newIndex].Name = chart.Name; // ✅ Name ensure karo

            this.fetchChartsData(chart, newIndex);

            const isFirstDrop = this.droppedCharts.filter(c => c !== null).length === 1;
            if (isFirstDrop) {
              this.createDashboard(chart, newIndex);
            } else {
              this.updateDashboard(chart);
            }
          });
        } else {
          return;
        }
      } else {
        this.$nextTick(() => {
          this.droppedCharts[newIndex] = chart;
          this.droppedCharts[newIndex].Name = chart.Name; // ✅ Name ensure karo

          this.fetchChartsData(chart, newIndex);

          const isFirstDrop = this.droppedCharts.filter(c => c !== null).length === 1;
          if (isFirstDrop) {
            this.createDashboard(chart, newIndex);
          } else {
            this.updateDashboard(chart);
          }
        });
      }
    }

    eventBus.emit("chart-Get-removed", chart);
  } catch (error) {
    console.error("Error parsing chart data:", error);
  }
}
,  
    updateDashboard() {
      const tenantId = getTenantPrefix() || "";
      const dashboardId = this.dashboard_id;
      this.loading = true;
      if (!dashboardId) {
    this.$emit("show-snackbar", "Dashboard can't be created");
    return;
  }
      const sanitizedChartsID = Array.isArray(this.droppedCharts)
        ? this.droppedCharts.filter((chart) => chart && chart.ID).map((chart) => chart.ID)
        : [];

      const requestBody = {
        Data: {
          dashboard: {
            name: this.name,
            chartsID: sanitizedChartsID,
            AccessLevel: this.accessLevel,
            isDefault: this.isDefault,
          },
        },
      };

      api
        .put(
          process.env.VUE_APP_API_URL + "public/api/dashboards/" + dashboardId,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": tenantId,
            },
          }
        )
        .then((response) => {
          if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
            if (this.name) {
              localStorage.setItem("dashboard", this.name)
              localStorage.setItem("newlyCreatedDashboard", this.name);
            }
            if (this.isDefault) {
              localStorage.setItem("defaultDashboard", this.name);
            }
            const getDashboard = localStorage.getItem("dashboard");
            const getDefault = localStorage.getItem("defaultDashboard");
            if (getDashboard === getDefault && this.isDefault === false) {
              localStorage.removeItem("defaultDashboard")
            }
            this.loading = false;
            this.successMessage = "Dashboard updated.";
            this.showSuccess = true;
          }
          else if (response && response.data && response.data.message) {
            console.error(response.data.message);
            this.errorMessage = response.data.message;
            this.showAlert = true;
          }
          else {
            console.error("Error while updating dashboard");
            this.errorMessage = "Error while updating dashboard";
            this.showAlert = true;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.errorMessage = "Error while updating dashboard";
          this.showAlert = true;
        })
        .finally(() => {
          this.hideExpand = false;
        });
    },
    createDashboard(chart, index) {
      const tenantId = getTenantPrefix() || "";

      const requestBody = {
        Data: {
          dashboard: {
            name: this.name,
            chartsID: [chart.ID],
            AccessLevel: this.accessLevel,
            isDefault: this.isDefault,
          },
        },
      };

      api
        .post(
          process.env.VUE_APP_API_URL + "public/api/dashboards",
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": tenantId,
            },
          }
        )
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 ||
              response.data.statusCode === 201)
          ) {
            this.dashboard_id = response.data.data.ID;
            const dashboardData = response.data.data;
            this.$emit("dashboard-id", dashboardData);
            if (this.name) {
              localStorage.setItem("dashboard", this.name)
            }
            if (this.isDefault) {
              localStorage.setItem("defaultDashboard", this.name);
            }
            this.successMessage = "Dashboard created successfully.";
            this.showSuccess = true;
          } else if (response && response.data && response.data.message) {
            this.errorMessage = response.data.message;
            this.showAlert = true;
          } else {
            this.errorMessage = "Error while creating dashboard";
            this.showAlert = true;
          }
        })
        .catch((error) => {
          this.errorMessage = error;
          this.showAlert = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },


    async fetchChartsData(chart, index) {
      if (!chart?.ID) {
        return;
      }
      if (typeof this.loading !== 'object') {
        this.loading = {};
      }
      this.loading[index] = true;

      const filterParams = buildFilterQueryParams(chart.Filter);
      const queryString = Object.entries(filterParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      const url = queryString
        ? `public/api/charts/data/${chart.ID}?${queryString}`
        : `public/api/charts/data/${chart.ID}`;

      const response = await api.get(url);

      if (
        response &&
        response.data &&
        (response.data.statusCode === 200 || response.data.statusCode === 201)
      ) {
        const samples = response.data.data?.samples;
        
        
        let rows = null;
        
        if (Array.isArray(samples)) {
          if (samples.length === 0) {
            rows = null; // Empty array means no data
          } else {
            rows = samples;
          }
        } 
        else if (samples && typeof samples === 'object') {
          if (Object.keys(samples).length === 0) {
            rows = null; // Empty object means no data
          } 
          else if ('rows' in samples) {
            rows = samples.rows;
          } 
          else {
            rows = null;
          }
        }
        
        if (rows != null && Array.isArray(rows) && rows.length > 0) {
          this.tableData[index] = rows.map((row) => {
            Object.keys(row).forEach((key) => {
              if (key + "_decimal" in row && row[key + "_decimal"] !== null) {
                const numValue = parseFloat(row[key]);
                if (!isNaN(numValue)) {
                  row[key] = numValue; // Keep as number for proper sorting and comparison
                }
              }
            });
            return row;
          });
          this.dataLoaded[index] = true;
          const xAxisColumn = chart.XAxisColumn;
          const capitalizedXAxisColumn = xAxisColumn;
          var xColumnDisplayName = await getDisplayNameDashboard(
            chart.TableType,
            capitalizedXAxisColumn
          );
          this.updatedXColumnDisplayName[index] =
            xColumnDisplayName.displayName;

          const yAxisColumns = chart.YAxisColumn
            ? chart.YAxisColumn.split(",").map(col => col.trim()).filter(col => col)
            : [];
          
          if (chart.GroupColumn && chart.YAxisColumn && yAxisColumns.length > 1 && yAxisColumns[yAxisColumns.length - 1] === chart.GroupColumn) {
            yAxisColumns.pop();
          }

          const capitalizedYAxisColumns = yAxisColumns;

          var yColumnDisplayNames = await Promise.all(
            capitalizedYAxisColumns.map((column) =>
              getDisplayNameDashboard(chart.TableType, column)
            )
          );

          this.updatedYColumnDisplayName[index] = yColumnDisplayNames
            .map(item => item.displayName)
            .join(', ');
            
          if (chart.GroupColumn) {
            const groupColumn = chart.GroupColumn;
            const capitalizedGroupColumn = groupColumn.charAt(0) + groupColumn.slice(1);
            const groupColumnDisplayName = await getDisplayNameDashboard(chart.TableType, capitalizedGroupColumn);
            this.updatedGroupColumnDisplayName[index] = groupColumnDisplayName.displayName;
          } else {
            this.updatedGroupColumnDisplayName[index] = "";
          }
            
            if (typeof this.loading !== 'object') {
        this.loading = {};
      }
      this.loading[index] = false;
          this.renderChart(chart, index, "chart_");
        }
        else {
          if (typeof this.loading !== 'object') {
            this.loading = {};
          }
          this.loading[index] = false;
          this.dataLoaded[index] = false;
          const plotName = chart?.Name || "this plot";
          const message = `Data is not available 1 for this plot (${plotName})`;
          const newNoData = [...this.noData];
          newNoData[index] = message;
          this.noData = newNoData;
          this.tableData[index] = [];
        }
      }
    },
    renderChart(chart, index, refPrefix) {
      if (!chart) {
        return;
      }

      this.$nextTick(() => {
        const chartRef = this.$refs[refPrefix + index];
        if (!chartRef) {
          return;
        }

        const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
        if (!chartDiv) return;

        if (!this.tableData[index] || this.tableData[index].length === 0) {
          const plotName = chart?.Name || "this plot";
          const newNoData = [...this.noData];
          newNoData[index] = `Data is not available 2 for this plot (${plotName})`;
          this.noData = newNoData;
          this.loading[index] = false;
          return;
        }

        const traces = this.generateTraces(chart, index);
        const layout = this.generateLayout(chart, index);
        const config = this.generateConfig(chart, index);
console.log("traces", traces);
        if (!traces || traces.length === 0) {
          const plotName = chart?.Name || "this plot";
          const newNoData = [...this.noData];
          newNoData[index] = `Data is not available 3 for this plot (${plotName})`;
          this.noData = newNoData;
          this.loading[index] = false;
          return;
        }

        if(chart.ChartType === "Scatter Plot" || chart.ChartType === "Line Plot") {
          layout.xaxis.type = chart?.XAxisScale || "linear";
          layout.yaxis.type = chart?.YAxisScale || "linear";
        } else if(chart.ChartType === "3D Scatter") {
          layout.scene.xaxis.type = chart?.XAxisScale || "linear";
          layout.scene.yaxis.type = chart?.YAxisScale || "linear";
        }

        const xData = this.tableData[index].map(item => item[chart.XAxisColumn]);
        if (xData && xData.length > 0) {
          const isAllStrings = xData.every(value => typeof value === 'string' && value !== null && value !== undefined);
          const isAllNumbers = xData.every(value => typeof value === 'number' && !isNaN(value) && value !== null && value !== undefined);
          
          if (isAllStrings && xData.length > 0) {
            const uniqueValues = [...new Set(xData)];
            if (uniqueValues.length <= 100) {
              if (chart.ChartType === "3D Scatter") {
                layout.scene.xaxis.type = 'category';
                layout.scene.xaxis.categoryorder = 'trace'; // Use 'trace' to preserve original order from data
              } else {
                layout.xaxis.type = 'category';
                layout.xaxis.categoryorder = 'trace'; // Use 'trace' to preserve original order from data
              }
            }
          }
          else if (isAllNumbers) {
            if (chart.ChartType === "3D Scatter") {
              layout.scene.xaxis.type = 'linear';
            } else {
              layout.xaxis.type = 'linear';
            }
          }
          else {
            void 0;
          }
        }

        this.cacheChartPlotData(index, traces, layout, config);

        Plotly.newPlot(chartDiv, traces, layout, config)
          .then(() => {
            this.teardownLegendScroll(index);
            this.legendScrollCleanups[index] = attachPlotlyLegendWheelScroll(chartDiv);
            this.setupLegendClickHandler(chartDiv);
          })
          .catch((error) => {
            console.error('Error creating plot:', error);
          });
      });
    },
    cacheChartPlotData(index, traces, layout, config) {
      this.chartPlotCache[index] = {
        traces,
        layout: {
          ...layout,
          autosize: true,
          height: 500,
          width: null,
        },
        config,
      };
    },
    plotModalChart(chart, index) {
      if (!chart) {
        return;
      }

      const chartRef = this.$refs["modal_chart_" + index];
      if (!chartRef) {
        return;
      }

      const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
      if (!chartDiv) {
        return;
      }

      if (!this.tableData[index] || this.tableData[index].length === 0) {
        return;
      }

      let chartData = this.chartPlotCache[index];
      if (!chartData) {
        const traces = this.generateTraces(chart, index);
        const layout = this.generateLayout(chart, index);
        const config = this.generateConfig(chart, index);
        if (!traces || traces.length === 0) {
          return;
        }
        chartData = {
          traces,
          layout: { ...layout, autosize: true, height: 500, width: null },
          config,
        };
      }

      const modalLayout = {
        ...chartData.layout,
        autosize: true,
        height: 500,
        width: null,
      };

      if (chart.ChartType === "Scatter Plot" || chart.ChartType === "Line Plot") {
        const xType = modalLayout.xaxis?.type === "category" ? "category" : (this.xAxisScaleModal === "log" ? "log" : "linear");
        modalLayout.xaxis = {
          ...(modalLayout.xaxis || {}),
          type: xType,
        };
        modalLayout.yaxis = {
          ...(modalLayout.yaxis || {}),
          type: this.yAxisScaleModal === "log" ? "log" : "linear",
        };
      } else if (chart.ChartType === "3D Scatter" && modalLayout.scene) {
        modalLayout.scene = {
          ...modalLayout.scene,
          xaxis: {
            ...(modalLayout.scene.xaxis || {}),
            type: this.xAxisScaleModal === "log" ? "log" : "linear",
          },
          yaxis: {
            ...(modalLayout.scene.yaxis || {}),
            type: this.yAxisScaleModal === "log" ? "log" : "linear",
          },
        };
      }

      if (modalLayout.legend) {
        modalLayout.legend.itemclick = false;
        modalLayout.legend.itemdoubleclick = false;
      }

      const modalConfig = {
        ...chartData.config,
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
        modeBarButtonsToRemove: ["lasso2d", "select2d", "pan2d", "resetscale", "zoom", "toImage"],
        modeBarButtonsToAdd: [
          {
            name: "Download plot as a png",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.camIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadPNG.bind(this, chart, index),
          },
          {
            name: "Download CSV",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.downloadIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.downloadCSV.bind(this, chart, index),
          },
        ],
      };

      this.$nextTick(() => {
        try {
          Plotly.purge(chartDiv);
        } catch (error) {
          console.warn("Error purging modal chart:", error);
        }

        Plotly.newPlot(chartDiv, chartData.traces, modalLayout, modalConfig)
          .then(() => {
            Plotly.Plots.resize(chartDiv).catch(() => {});
            attachPlotlyLegendWheelScroll(chartDiv);
            this.setupLegendClickHandler(chartDiv);
            chartDiv.on("plotly_legendclick", () => false);
            chartDiv.on("plotly_legenddoubleclick", () => false);
          })
          .catch((error) => {
            console.error("Error creating modal plot:", error);
          });
      });
    },
    generateTraces(chart, index) {
      if (!chart) {
        return [];
      }

      if (!chart.ChartType) {
        return [];
      }

      if (!this.tableData[index] || this.tableData[index].length === 0) {
        return [];
      }

      const traceType = this.getTraceType(chart);
      const traceMode = this.getTraceMode(chart, index);
      const scatterMarkerSizes = 8;
      const yColumns = chart.YAxisColumn ? chart.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) : [];
      this.selectedYAxisKeys[index] = yColumns;

      console.log(`[DashboardCreate.generateTraces] YAxisColumn processing for chart ${index}:`, {
        YAxisColumn: chart.YAxisColumn,
        yColumns: yColumns,
        GroupColumn: chart.GroupColumn,
        yColumnsLength: yColumns.length
      });

      if (chart.GroupColumn && chart.YAxisColumn) {
        if (yColumns.length > 1 && yColumns[yColumns.length - 1] === chart.GroupColumn) {
          yColumns.pop();
          console.log(`[DashboardCreate.generateTraces] Removed GroupColumn from yColumns for chart ${index}`);
        }
        this.selectedColorGroupKeys[index] = chart.GroupColumn;
        this.selectedXAxisKeys[index] = chart.XAxisColumn;
        this.selectedZAxisKeys[index] = chart.ZAxisColumn;
        this.xBins[index] = chart.NumXBins;
        this.yBins[index] = chart.NumYBins;

        const colorGroupData = this.tableData[index].map(item => item[chart.GroupColumn]);
        const uniqueValues = [...new Set(colorGroupData.filter(item => item !== undefined && item !== null))];

        const groupedYAxisData = {};
        const groupedXAxisData = {};
        const groupedZAxisData = {};

        uniqueValues.forEach(value => {
          groupedYAxisData[value] = [];
          groupedXAxisData[value] = [];
          groupedZAxisData[value] = [];
        });

        this.tableData[index].forEach(row => {
          const colorGroupValue = row[this.selectedColorGroupKeys[index]];

          if (uniqueValues.includes(colorGroupValue)) {
            const yAxisRowData = this.selectedYAxisKeys[index].map(key => row[key]);
            groupedYAxisData[colorGroupValue].push(yAxisRowData);

            const xAxisRowData = row[this.selectedXAxisKeys[index]];
            groupedXAxisData[colorGroupValue].push(xAxisRowData);

            const zAxisRowData = row[this.selectedZAxisKeys[index]];
            groupedZAxisData[colorGroupValue].push(zAxisRowData);
          }
        });

        const idGroups = {};
        
        this.tableData[index].forEach((row) => {
          const dataId = row.uniqueID;
          const legend = row[chart.GroupColumn];
          
          if (!idGroups[dataId]) {
            idGroups[dataId] = {
              data: [],
              legend: legend
            };
          }
          
          const xVal = row[chart.XAxisColumn];
          const yVal = yColumns.length > 0 ? row[yColumns[0]] : null;
          const zVal = chart.ZAxisColumn ? row[chart.ZAxisColumn] : null;
          
          if (xVal !== null && xVal !== undefined && xVal !== '' && 
              yVal !== null && yVal !== undefined && yVal !== '' && 
              !isNaN(yVal)) { // Only check yVal for NaN since xVal can be string
            
            let processedXVal = xVal;
            if (typeof xVal === 'string' && !isNaN(parseFloat(xVal))) {
              processedXVal = parseFloat(xVal);
            }
            
            idGroups[dataId].data.push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
          }
        });
        
       
        
        const traces = [];
        let colorIndex = 0;
        const shownLegends = new Set(); // Track which legends we've already shown
        
        const legendColorMap = {};
        
        Object.keys(idGroups).forEach((idKey, index) => {
          const idGroup = idGroups[idKey];
          const validData = idGroup.data;
          const legend = idGroup.legend;
          const markerSize = (chart.ChartType === "Scatter Plot") ? scatterMarkerSizes : (this.markerSizes && this.markerSizes.length > 0 ? this.markerSizes : [10]);
          
          if (validData.length === 0) {
            console.warn(`[DashboardCreate.generateTraces] ⚠️ Skipping trace for idGroup '${idKey}' in chart ${index} (${chart?.Name}): No valid data points after processing. All rows were filtered out.`);
            return;
          }

          if (!legendColorMap[legend]) {
            legendColorMap[legend] = this.getColorForIndex(Object.keys(legendColorMap).length % 6);
          }
          const traceColor = legendColorMap[legend];

          const isFirstOfType = !shownLegends.has(legend);
          if (isFirstOfType) {
            shownLegends.add(legend);
          }

          const isXNumeric = validData.every(item => typeof item.x === 'number' && !isNaN(item.x));
          let sortedData;
          
          if (isXNumeric) {
            sortedData = validData.sort((a, b) => a.x - b.x);
          } else {
            sortedData = validData;
          }
          
          const validXData = sortedData.map(item => item.x);
          const validYData = sortedData.map(item => item.y);
          const validZData = sortedData.map(item => item.z);

          const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

          const trace = {
            x: validXData,
            y: processedYData,
            z: validZData,
            type: traceType,
            mode: traceMode,
            marker: {
              size: markerSize,
              color: traceColor // Use the same color for all traces of the same legend type
            },
            name: legend, // Use legend type as name
            legendgroup: legend, // Group by legend type
            showlegend: isFirstOfType, // Show only one legend entry per type
            connectgaps: false,
            customdata: validXData.map((xValue, dataIndex) => {
              const item = sortedData[dataIndex];
              return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
            }),
            hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
          };

          if (chart.ChartType === "Bubble" || chart.ChartType === "3D Scatter") {
            const markerSizes = this.markerSizes && this.markerSizes.length > 0 ? this.markerSizes : [10];
            const maxMarkerSize = Math.max(...markerSizes);
            trace.marker = {
              ...trace.marker,
              sizemode: 'area',
              sizeref: 2.0 * maxMarkerSize / (80 * 2), // Adjusted size reference for better fitting
              sizemin: 2,
            };
          }

            if (["2D Histogram"].includes(chart.ChartType)) {
              trace.autobinx = false;
              trace.autobiny = false;
              trace.xbins = { 
                size: this.calculateBinSize(validXData, this.xBins[index]),
                start: Math.min(...validXData),
                end: Math.max(...validXData)
              };
              trace.ybins = { 
                size: this.calculateBinSize(processedYData, this.yBins[index]),
                start: Math.min(...processedYData),
                end: Math.max(...processedYData)
              };
            }

            traces.push(trace);
            
            shownLegends.add(legend);
            colorIndex++;
          });

        return traces.filter(trace => trace !== null);
      } else {
        const xData = this.tableData[index].map(item => item[chart.XAxisColumn]);
        this.xBins[index] = chart.NumXBins;

        const isInvalidData = (data) => !data || data.every(value => value === null || value === undefined);

        if (isInvalidData(xData)) {
          return [];
        }

        if (chart.ChartType === "Histogram") {
          const validXData = xData.filter(value => 
            value !== null && 
            value !== undefined && 
            value !== ''
          );
          
          if (validXData.length === 0) {
            return [];
          }
          
          const isNumericData = validXData.every(value => typeof value === 'number' && !isNaN(value));
          
          let sortedData, minVal, maxVal;
          
          if (isNumericData) {
            sortedData = validXData.sort((a, b) => a - b);
            minVal = Math.min(...sortedData);
            maxVal = Math.max(...sortedData);
          } else {
            sortedData = validXData;
            minVal = 0;
            maxVal = sortedData.length;
          }
          
          if (chart.GroupColumn) {
            const hasGroupColumn = this.tableData[index][0] && chart.GroupColumn in this.tableData[index][0];
            
            if (hasGroupColumn) {
              const groupedData = {};
              this.tableData[index].forEach(row => {
                const groupValue = row[chart.GroupColumn];
                const xValue = row[chart.XAxisColumn];
                
                if (groupValue !== null && groupValue !== undefined && 
                    xValue !== null && xValue !== undefined && xValue !== '') {
                  if (!groupedData[groupValue]) {
                    groupedData[groupValue] = [];
                  }
                  groupedData[groupValue].push(xValue);
                }
              });

              if (Object.keys(groupedData).length > 0) {
                let unifiedXbinsConfig = undefined;
                let isNumericData = false;
                
                const allXData = [];
                for (const key of Object.keys(groupedData)) {
                  const xData = groupedData[key];
                  const validXData = xData.filter(value => 
                    value !== null && 
                    value !== undefined && 
                    value !== ''
                  );
                  allXData.push(...validXData);
                }
                
                if (allXData.length > 0) {
                  isNumericData = allXData.every(value => {
                    if (typeof value === 'number') {
                      return !isNaN(value);
                    }
                    if (typeof value === 'string') {
                      const num = parseFloat(value);
                      return !isNaN(num) && isFinite(num);
                    }
                    return false;
                  });
                  
                  if (isNumericData) {
                    const numericData = allXData.map(value => {
                      if (typeof value === 'string') {
                        return parseFloat(value);
                      }
                      return value;
                    });
                    
                    const minVal = Math.min(...numericData);
                    const maxVal = Math.max(...numericData);
                    const range = maxVal - minVal;
                    
                    if (range > 0) {
                      const adjustedMinVal = Math.max(0, minVal);
                      const adjustedRange = maxVal - adjustedMinVal;
                      
                      const padding = adjustedRange * 0.001; // 0.1% padding
                      const binSize = (adjustedRange + 2 * padding) / this.xBins[index];
                      
                      unifiedXbinsConfig = {
                        start: Math.max(0, adjustedMinVal - padding), // Ensure start is never negative
                        end: maxVal + padding,
                        size: binSize
                      };
                    }
                  }
                }

                const traces = [];
                const uniqueGroups = Object.keys(groupedData).sort(); // Sort for consistent color assignment
                let colorIndex = 0;

                uniqueGroups.forEach(groupValue => {
                  const groupData = groupedData[groupValue];
                  if (groupData.length === 0) return;

                  const isGroupNumeric = groupData.every(value => typeof value === 'number' && !isNaN(value));
                  const sortedGroupData = isGroupNumeric ? groupData.sort((a, b) => a - b) : groupData;

                  const trace = {
                    x: sortedGroupData,
                    type: traceType,
                    autobinx: false,
                    nbinsx: this.xBins[index],
                    xbins: unifiedXbinsConfig, // Use unified bin boundaries for all groups
                    marker: {
                      color: this.getColorForIndex(colorIndex % 6),
                      line: {
                        color: "rgba(0, 0, 0, 1)",
                        width: 1
                      }
                    },
                    name: groupValue,
                    showlegend: true,
                    customdata: sortedGroupData.map((value, dataIndex) => {
                      return groupValue; // Return the legend group name (e.g., "FD", "ML", "AI", "AGI")
                    }),
                    hovertemplate: `
  <b>${groupValue}</b><br>
  Range: %{x}<br>
  Count: %{y}
  <extra></extra>
`,
                  };

                  traces.push(trace);
                  colorIndex++;
                });

                return traces;
              }
            }
          }
          
          const barColor = "rgba(0, 100, 250, 0.6)";
          const borderColor = "rgba(0, 0, 0, 1)";
          const borderWidth = 1;

          const trace = {
            x: sortedData,
            type: traceType,
            autobinx: false,
            nbinsx: this.xBins[index], // Use exact number of bins
            xbins: isNumericData ? {
              start: minVal,
              end: maxVal,
              size: (maxVal - minVal) / this.xBins[index]
            } : undefined, // Let Plotly handle categorical binning
            marker: {
              color: barColor,
              line: {
                color: borderColor,
                width: borderWidth
              }
            },
            showlegend: false, // No legend for single histogram
            hovertemplate: `
  Range: %{x}<br>
  Count: %{y}
  <extra></extra>
`,
          };

          return [trace];
        }

        if (chart.ChartType === "Line Plot" || chart.ChartType === "Scatter Plot") {
          const idGroups = {};
          
          this.tableData[index].forEach((row) => {
            const dataId = row.uniqueID;
            
            if (!idGroups[dataId]) {
              idGroups[dataId] = [];
            }
            
            const xVal = row[chart.XAxisColumn];
            const yVal = yColumns.length > 0 ? row[yColumns[0]] : null;
            const zVal = chart.ZAxisColumn ? row[chart.ZAxisColumn] : null;
            
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '' && 
                !isNaN(yVal)) {
              
              let processedXVal = xVal;
              if (typeof xVal === 'string' && !isNaN(parseFloat(xVal))) {
                processedXVal = parseFloat(xVal);
              }
              
              idGroups[dataId].push({ x: processedXVal, y: yVal, z: zVal, originalRow: row });
            }
          });
          
          const traces = [];
          const singleColor = "rgba(0, 100, 250, 0.8)"; // Same blue color for all
          
          Object.keys(idGroups).forEach((idKey) => {
            const validData = idGroups[idKey];
            
            if (validData.length === 0) {
              return;
            }

            const sortedData = validData.sort((a, b) => a.x - b.x);
            const validXData = sortedData.map(item => item.x);
            const validYData = sortedData.map(item => item.y);
            const validZData = sortedData.map(item => item.z);

            const processedYData = validYData.map(item => Array.isArray(item) ? item[0] : item);

            const trace = {
              x: validXData,
              y: processedYData,
              z: validZData,
              type: traceType,
              mode: traceMode,
              marker: {
                size: (chart.ChartType === "Scatter Plot") ? scatterMarkerSizes : (this.markerSizes && this.markerSizes.length > 0 ? this.markerSizes : [10]),
                color: singleColor
              },
              name: `Data Series ${Object.keys(idGroups).indexOf(idKey) + 1}`, // Generic name
              showlegend: false, // No legend since it's not really a legend
              connectgaps: false,
              customdata: validXData.map((xValue, dataIndex) => {
                const item = sortedData[dataIndex];
                return item && item.originalRow && item.originalRow.caption ? item.originalRow.caption : 'N/A';
              }),
              hovertemplate: `
%{x}  %{y}  %{customdata}
<extra></extra>
`,
            };

            traces.push(trace);
          });

          return traces.filter(trace => trace !== null);
        }

        const yData = yColumns.map(col => this.tableData[index].map(item => item[col]));
        this.yBins[index] = chart.NumYBins;
        const zData = this.tableData[index].map(item => item[chart.ZAxisColumn]);
        this.markerSizes = [...zData];

        const markerSize = (chart.ChartType === "Scatter Plot")
          ? scatterMarkerSizes
          : (this.markerSizes && this.markerSizes.length > 0 ? this.markerSizes : [10]);

        return yData.map((y, i) => {
          if (isInvalidData(y)) {
            return null;
          }

          const validData = xData.map((xVal, j) => {
            const yVal = y[j];
            if (xVal !== null && xVal !== undefined && xVal !== '' && 
                yVal !== null && yVal !== undefined && yVal !== '' && 
                !isNaN(yVal)) {
              return { x: xVal, y: yVal, index: j };
            }
            return null;
          }).filter(item => item !== null);

          if (validData.length === 0) {
            return null;
          }

          const isXNumeric = validData.every(item => typeof item.x === 'number' && !isNaN(item.x));
          const sortedData = isXNumeric 
            ? validData.sort((a, b) => a.x - b.x)
            : validData; // Keep original order for string data

          const trace = {
            x: sortedData.map(item => item.x),
            y: sortedData.map(item => item.y),
            z: zData,
            type: traceType,
            mode: traceMode,
            name: this.updatedYColumnDisplayName[index] ? 
              this.updatedYColumnDisplayName[index].split(", ")[i] || yColumns[i] : 
              yColumns[i],
            marker: { 
              size: markerSize,
              ...(chart.ChartType !== 'Histogram' && !chart.GroupColumn ? {
                color: 'rgba(0, 100, 250, 0.8)' // Blue color for all plots without legend
              } : {})
            },
          };

          if (chart.ChartType === "Bubble" || chart.ChartType === "3D Scatter") {
            const markerSizes = this.markerSizes && this.markerSizes.length > 0 ? this.markerSizes : [10];
            const maxMarkerSize = Math.max(...markerSizes);
            trace.marker = {
              ...trace.marker,
              sizemode: 'area',
              sizeref: 2.0 * maxMarkerSize / (80 * 2), // Adjusted size reference for better fitting
              sizemin: 2,
            };
          }

          if (chart.ChartType === "2D Histogram") {
            trace.colorscale = [
              [0.0, 'rgba(247,251,255,255)'],
              [0.25, 'rgba(193,217,237,255)'],
              [0.5, 'rgba(97,167,210,255)'],
              [0.75, 'rgba(24,101,172,255)'],
              [1.0, 'rgba(8,48,108,255)']
            ];
            trace.colorbar = {
              title: {
                text: 'Number of Samples', // Title for the color bar
                side: 'right',
              }
            };
          }

          if (["2D Histogram"].includes(chart.ChartType)) {
            trace.autobinx = false;
            trace.autobiny = false;
            trace.xbins = { 
              size: this.calculateBinSize(sortedData.map(item => item.x), this.xBins[index]),
              start: Math.min(...sortedData.map(item => item.x)),
              end: Math.max(...sortedData.map(item => item.x))
            };
            trace.ybins = { 
              size: this.calculateBinSize(sortedData.map(item => item.y), this.yBins[index]),
              start: Math.min(...sortedData.map(item => item.y)),
              end: Math.max(...sortedData.map(item => item.y))
            };
          }

          trace.customdata = sortedData.map((item, dataIndex) => {
            const originalIndex = item.index;
            if (this.tableData[index] && this.tableData[index][originalIndex] && this.tableData[index][originalIndex].caption) {
              return this.tableData[index][originalIndex].caption;
            }
            return 'N/A';
          });
          trace.hovertemplate = `
%{x}  %{y}  %{customdata}
<extra></extra>
`;

          return trace;
        }).filter(trace => trace !== null);
      }
    },

    calculateBinSize(data, bins) {
      if (!data || data.length === 0 || !bins || bins < 1) {
        return 1;
      }
      
      const validData = data.filter(value => 
        value !== null && 
        value !== undefined && 
        value !== '' && 
        !isNaN(value)
      );
      
      if (validData.length === 0) {
        return 1;
      }
      
      const numericData = validData.filter(value => typeof value === 'number');
      if (numericData.length > 0) {
        const minValue = Math.min(...numericData);
        const maxValue = Math.max(...numericData);
        const range = maxValue - minValue;
        
        if (range === 0) {
          return 1;
        }
        
        const binSize = range / bins;
        
        const minBinSize = range / 100; // Maximum 100 bins
        const finalBinSize = Math.max(binSize, minBinSize);
        
        return finalBinSize;
      }
      
      const uniqueValues = [...new Set(validData)];
      const uniqueCount = uniqueValues.length;
      
      const optimalBins = Math.min(bins, uniqueCount);
      
      return Math.max(uniqueCount / optimalBins, 1);
    },

    generateLayout(chart, index) {
      if (!chart) {
        return {};
      }

      if (!chart.ChartType) {
        return {};
      }

      const yColumns = chart.YAxisColumn ? chart.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) : [];
      if (chart.GroupColumn && chart.YAxisColumn) {
        if (yColumns.length > 1 && yColumns[yColumns.length - 1] === chart.GroupColumn) {
          yColumns.pop(); // Remove the last Y-axis column if GroupColumn is present
        }
      }

      const baseLayout = {
        margin: {
          l: 55,
          r: 0,
          b: 100
        },
        ...(chart.ChartType === "Histogram" ? {
          plot_bgcolor: this.isDarkMode ? 'transparent' : '#e5ecf6',  
          paper_bgcolor: this.isDarkMode ? 'transparent' : '#ffffff',
        } : {}),
        legend: {
          title: {
            text: this.selectedColorGroupKeys[index] ? (this.updatedGroupColumnDisplayName[index] || this.selectedColorGroupKeys[index]) : ''
          },
          visible: (this.selectedColorGroupKeys[index] && this.selectedColorGroupKeys[index].trim() !== '') || 
                   (chart.ChartType === "Histogram" && chart.GroupColumn) ? true : false,
          orientation: "v",
          x: 1.02,
          xanchor: "left",
          y: 0.5,
          yanchor: "middle",
          tracegroupgap: 6,
          itemwidth: 30,
        },
      };

      const layout = {
        ...(chart.ChartType === "3D Scatter" ? {
          scene: {
            xaxis: {
              title: { 
                text: this.formatAxisTitle(
                  this.updatedXColumnDisplayName[index] || chart.XAxisColumn,
                  this.getUOMForProperty(chart.XAxisColumn, index)
                )
              },
              tickangle: -45,
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              type: 'auto',
              tickmode: 'auto',
              nticks: undefined,
              tickformat: this.getFormattedTickFormat(chart, index, 'x'),
              showgrid: true,
              gridcolor: chartGridColor(this.isDarkMode),
              gridwidth: 0.5,
            },
            yaxis: {
              title: {
                text: this.formatAxisTitle(
                  this.updatedYColumnDisplayName[index]?.replace(/, /g, "<br>") || yColumns.join(", ")?.replace(/, /g, "<br>"),
                  this.getUOMForProperty(yColumns[0], index)
                ),
                standoff: 15,
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              type: 'auto',
              tickmode: 'auto',
              nticks: undefined,
              tickformat: this.getFormattedTickFormat(chart, index, 'y'),
              showgrid: true,
              gridcolor: chartGridColor(this.isDarkMode),
              gridwidth: 0.5,
            },
            zaxis: {
              title: {
                text: this.formatAxisTitle(
                  chart.ZAxisColumn || "Z",
                  this.getUOMForProperty(chart.ZAxisColumn, index)
                ),
                standoff: 15,
              },
              tickfont: { size: 10 },
              automargin: true,
              autorange: true,
              type: 'auto',
              tickmode: 'auto',
              nticks: undefined,
              tickformat: this.getFormattedTickFormat(chart, index, 'z'),
              showgrid: true,
              gridcolor: chartGridColor(this.isDarkMode),
              gridwidth: 0.5,
            },
            camera: {
              eye: { x: 1.5, y: 1.5, z: 1.5 },
              center: { x: 0, y: 0, z: 0 },
              up: { x: 0, y: 0, z: 1 }
            },
            aspectmode: 'auto',
            aspectratio: { x: 1, y: 1, z: 1 }
          }
        } : {
          xaxis: {
            title: { 
              text: this.formatAxisTitle(
                this.updatedXColumnDisplayName[index] || chart.XAxisColumn,
                this.getUOMForProperty(chart.XAxisColumn, index)
              )
            },
            tickangle: -45,
            tickfont: { size: 10 },
            automargin: true,
            autorange: true,
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat(chart, index, 'x'),
            showgrid: true,
            gridcolor: chartGridColor(this.isDarkMode),
            gridwidth: 0.5,
          },
          yaxis: { 
            title: {
              text: chart.ChartType === "Histogram"
                ? "No. of Samples"
                : this.formatAxisTitle(
                    this.updatedYColumnDisplayName[index]?.replace(/, /g, "<br>") || yColumns.join(", ")?.replace(/, /g, "<br>"),
                    this.getUOMForProperty(yColumns[0], index)
                  ),
              standoff: 15,
            },
            tickfont: { size: 10 },
            automargin: true,
            autorange: true,
            type: 'auto',
            tickmode: 'auto',
            nticks: undefined,
            tickformat: this.getFormattedTickFormat(chart, index, 'y'),
            showgrid: true,
            gridcolor: chartGridColor(this.isDarkMode),
            gridwidth: 0.5,
          },
        }),
        ...baseLayout,
      };

      if (this.tableData[index] && this.tableData[index].length > 0) {
        const xData = this.tableData[index].map(item => item[chart.XAxisColumn]);
        if (xData && xData.length > 0) {
          const isAllStrings = xData.every(value => typeof value === 'string' && value !== null && value !== undefined);
          const isAllNumbers = xData.every(value => typeof value === 'number' && !isNaN(value) && value !== null && value !== undefined);
          
          if (isAllStrings && xData.length > 0) {
            const uniqueValues = [...new Set(xData)];
            if (uniqueValues.length <= 100) {
              if (chart.ChartType === "3D Scatter") {
                layout.scene.xaxis.type = 'category';
                layout.scene.xaxis.categoryorder = 'trace'; // Use 'trace' to preserve original order from data
                layout.scene.xaxis.tickmode = 'auto';
                layout.scene.xaxis.nticks = undefined;
                layout.scene.xaxis.autorange = true;
              } else {
                layout.xaxis.type = 'category';
                layout.xaxis.categoryorder = 'trace'; // Use 'trace' to preserve original order from data
                layout.xaxis.tickmode = 'auto';
                layout.xaxis.nticks = undefined;
                layout.xaxis.autorange = true;
              }
            }
          }
          else if (isAllNumbers) {
            if (chart.ChartType === "3D Scatter") {
              layout.scene.xaxis.type = chart?.XAxisScale || 'linear';
              layout.scene.xaxis.autorange = true;
              layout.scene.xaxis.tickmode = 'auto';
              layout.scene.xaxis.nticks = undefined;
              layout.scene.xaxis.tickformat = this.getFormattedTickFormat(chart, index, 'x');
            } else {
              layout.xaxis.type = chart?.XAxisScale || 'linear';
              layout.xaxis.autorange = true;
              layout.xaxis.tickmode = 'auto';
              layout.xaxis.nticks = undefined;
              layout.xaxis.tickformat = this.getFormattedTickFormat(chart, index, 'x');
            }
          }
          else {
            if (chart.ChartType === "3D Scatter") {
              layout.scene.xaxis.autorange = true;
            } else {
              layout.xaxis.autorange = true;
            }
          }
        }
      }

      if (chart.ChartType === "Histogram" || chart.ChartType === "2D Histogram") {
        if (this.tableData[index] && this.tableData[index].length > 0) {
          const xData = this.tableData[index].map(item => item[chart.XAxisColumn]);
          if (xData && xData.length > 0) {
            const isAllNumbers = xData.every(value => typeof value === 'number' && !isNaN(value) && value !== null && value !== undefined);
            if (isAllNumbers && !layout.xaxis.type) {
              layout.xaxis.type = chart?.XAxisScale || 'linear';
            }
          }
        }
        if (this.tableData[index] && this.tableData[index].length > 0 && chart.YAxisColumn) {
          const yColumns = chart.YAxisColumn ? chart.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) : [];
          const yData = yColumns.map(col => this.tableData[index].map(item => item[col])).flat();
          if (yData && yData.length > 0) {
            const isAllNumbers = yData.every(value => typeof value === 'number' && !isNaN(value) && value !== null && value !== undefined);
            if (isAllNumbers && !layout.yaxis.type) {
              layout.yaxis.type = chart?.YAxisScale || 'linear';
            }
          }
        }
      }

      return layout;
    },
    generateConfig(chart, index) {
      const config = {
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
      };

      const expandButton = {
        name: "Expand",
        icon: {
          svg: `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <image href="${Expand}" width="24" height="24"/>
            </svg>
          `,
        },
        click: () => {
          this.openModal(chart, index);
        },
      };

      if (this.isEditing) {
        config.modeBarButtonsToRemove = [
          "lasso2d", "select2d", "pan2d", "resetscale", "zoom", "toImage", "toggleSpikelines", 
          "hoverClosestCartesian", "hoverCompareCartesian", "zoomIn", "zoomOut", "autoScale",
          "pan", "select", "lasso", "box", "autoScale2d", "resetScale2d", "hoverClosestGl2d",
          "hoverClosestPie", "toggleHover", "resetViews", "toggleSpikelines", "resetViewMapbox"
        ];
        config.modeBarButtonsToAdd = [
          expandButton,
          {
            name: "Remove from Dashboard",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.cross}" width="24" height="24"/>
                </svg>
              `,
            },
            click: () => {
              this.removePlotFromIndex(index, chart);
            },
          },
        ];
      } else {
        config.modeBarButtonsToRemove = [
          "lasso2d", "select2d", "pan2d", "resetscale", "zoom", "toImage", "toggleSpikelines", 
          "hoverClosestCartesian", "hoverCompareCartesian", "zoomIn", "zoomOut", "autoScale",
          "pan", "select", "lasso", "box", "autoScale2d", "resetScale2d", "hoverClosestGl2d",
          "hoverClosestPie", "toggleHover", "resetViews", "toggleSpikelines", "resetViewMapbox"
        ];
        config.modeBarButtonsToAdd = [
          {
            name: "Remove from Dashboard",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${this.cross}" width="24" height="24"/>
                </svg>
              `,
            },
            click: () => {
              this.removePlotFromIndex(index, chart);
            },
          },
        ];
      }

      return config;
    },
    getTraceType(chart) {
      if (!chart || !chart.ChartType) {
        return "scatter"; // Return default instead of null
      }
      switch (chart.ChartType) {
        case "Line Plot": return "scatter";
        case "Bar Chart": return "bar";
        case "Histogram": return "histogram";
        case "Bubble": return "scatter";
        case "3D Scatter": return "scatter3d";
        case "Scatter Plot": return "scatter";
        case "2D Histogram": return "histogram2d";
        case "Heatmap": return "heatmap";
        case "Treemap": return "treemap";
        default: return "scatter";
      }
    },

    getTraceMode(chart, index) {
      const isLargeDataset = this.tableData[index] && this.tableData[index].length > 1000;
      
      if (chart.ChartType === "Bubble") return "markers";
      if (chart.ChartType === "Scatter Plot") return "markers";
      if (chart.ChartType === "3D Scatter") return "markers";
      if (chart.ChartType === "Line Plot") {
        return "lines";
      }
      return undefined;
    },
    openModal(chart, index) {
      if (!chart) {
        console.warn('Chart is undefined for index:', index);
        return;
      }
      
      this.xAxisScaleModal = chart.XAxisScale || "linear";
      this.yAxisScaleModal = chart.YAxisScale || "linear";
      if (this.xAxisScaleModal === "log") {
        this.isModalXLog = true;
      }
      else {
        this.isModalXLog = false;
      }
      if (this.yAxisScaleModal === "log") {
        this.isModalYLog = true;
      }
      else {
        this.isModalYLog = false;
      }
      
      this.expandedIndex = index;
      this.isPreviewOpen[index] = true;

      this.$nextTick(() => {
        this.plotModalChart(chart, index);
      });
    },

    closeModal(index) {
      this.isPreviewOpen[index] = false;
    },

    resizeChart(index, refPrefix) {
      this.$nextTick(() => {
        const chartRef = this.$refs[refPrefix + index];
        
        if (!chartRef) {
          console.warn(`Chart reference for ${refPrefix + index} is undefined.`);
          return;
        }

        const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
        if (!chartDiv) return;

        Plotly.Plots.resize(chartDiv); // Resize the chart
      });
    },
    resizeAllCharts() {
      this.$nextTick(() => {
        for (let i = 0; i < this.droppedCharts.length; i++) {
          if (!this.droppedCharts[i]) continue; // Skip if chart is null or undefined
          
          const chartRef = this.$refs['chart_' + i];
          if (!chartRef) {
            continue;
          }

          const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
          if (!chartDiv) return;

          Plotly.Plots.resize(chartDiv); // Resize the chart
        }
      });
    },

    handleDownloadCSV(index, chart) {
      const chartData = this.tableData[index];
      const chartName = this.droppedCharts[index].Name;
      const csvContent = this.convertToCsv(chartData, chartName);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const fileName = `${chartName}.csv`;

      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, fileName);
      } else {
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", fileName);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    },

    downloadPNG(chart, index) {
      const chartRef = this.$refs['chart_' + index];
      if (!chartRef) {
        return;
      }

      const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
      if (!chartDiv) {
        return;
      }

      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      document.body.appendChild(tempDiv);

      const traces = this.generateTraces(chart, index);
      const layoutWithTitle = this.generateLayout(chart, index);
      layoutWithTitle.title = {
        text: chart.Name,
        font: {
          size: 16,
          color: '#333'
        },
        x: 0.5,
        y: 0.95
      };

      Plotly.newPlot(tempDiv, traces, layoutWithTitle, {
        displayModeBar: false,
        responsive: true
      }).then(() => {
        Plotly.downloadImage(tempDiv, {
          format: 'png',
          filename: chart.Name || 'chart',
          height: 600,
          width: 800
        }).then(() => {
          document.body.removeChild(tempDiv);
        });
      });
    },

    getDecimalPrecision(chart, columnName) {
      if (this.tableData && this.tableData.length > 0) {
        const firstRow = this.tableData[0];
        const decimalKey = `${columnName}_decimal`;
        if (firstRow[decimalKey] !== undefined && firstRow[decimalKey] !== null) {
          const precision = firstRow[decimalKey];
          if (precision === 0) {
            return 0;
          }
          return Math.min(precision, 6); // Cap at 6 decimal places
        }
      }
      return 2; // Default to 2 decimal places
    },

    shouldFormatAxisData(chart, index, axisType) {
      if (!this.tableData[index] || this.tableData[index].length === 0) {
        return false;
      }

      let columnName;
      if (axisType === 'x') {
        columnName = chart.XAxisColumn;
      } else if (axisType === 'y') {
        const yColumns = chart.YAxisColumn ? chart.YAxisColumn.split(",").map(col => col.trim()).filter(col => col) : [];
        if (chart.GroupColumn && chart.YAxisColumn) {
          yColumns.pop();
        }
        columnName = yColumns[0];
      } else if (axisType === 'z') {
        columnName = chart.ZAxisColumn;
      }

      if (!columnName) return false;

      const values = this.tableData[index]
        .map(item => item[columnName])
        .filter(value => typeof value === 'number' && !isNaN(value));

      return shouldFormatNumbers(values);
    },

    getFormattedTickFormat(chart, index, axisType) {
      const decimalPrecision = this.getDecimalPrecision(chart, 
        axisType === 'x' ? chart.XAxisColumn : 
        axisType === 'y' ? (chart.YAxisColumn ? chart.YAxisColumn.split(",")[0] : '') :
        chart.ZAxisColumn
      );

      if (this.shouldFormatAxisData(chart, index, axisType)) {
        return createCustomTickFormatter(decimalPrecision);
      }
      
      return `.${decimalPrecision}~f`;
    },

    getUOMForProperty(columnName, index) {
      if (this.tableData && this.tableData[index] && this.tableData[index].length > 0) {
        const firstRow = this.tableData[index][0];
        const uomKey = `${columnName}_UOM`;
        if (firstRow && firstRow[uomKey] && firstRow[uomKey] !== 'data-not-available') {
          return firstRow[uomKey];
        }
      }
      return null;
    },

    formatAxisTitle(displayName, uom) {
      if (uom && uom.trim() !== '') {
        return `${displayName} (${uom})`;
      }
      return displayName;
    },

    convertToCsv(data, chartName) {
      const csvRows = [];
      const headers = ['Sample ID', 'Asset', 'Field', 'Well', 'Fluid Sample ID'].concat(Object.keys(data[0]).filter(header => header !== 'caption' && header !== '_id' && header !== 'uniqueID' && !header.endsWith('_decimal') && header !== 'field' && header !== 'asset'&& header !== 'well'));
      csvRows.push(headers.join(","));

      for (const row of data) {
        const values = headers.map((header) => {
          if (['Asset', 'Field', 'Well', 'Fluid Sample ID'].includes(header)) {
            return row.caption.split(',')[headers.indexOf(header) - 1];
          }
          if (header === 'Sample ID') {
            return row._id;
          }
          return JSON.stringify(row[header], (key, value) => value === null ? "" : value);
        });
        csvRows.push(values.join(","));
      }

      return csvRows.join("\n");
    },

  removePlotFromIndex(index, chart) {
    this.droppedCharts.splice(index, 1, null);
    this.tableData.splice(index, 1, []);
    this.selectedColorGroupKeys.splice(index, 1, '');
    this.selectedYAxisKeys.splice(index, 1, []);
    this.selectedXAxisKeys.splice(index, 1, []);
    this.selectedZAxisKeys.splice(index, 1, []);
    this.updatedXColumnDisplayName.splice(index, 1, '');
    this.updatedYColumnDisplayName.splice(index, 1, '');
    this.updatedGroupColumnDisplayName.splice(index, 1, '');
    this.isPreviewOpen.splice(index, 1, false);
    this.xBins.splice(index, 1, []);
    this.yBins.splice(index, 1, []);
    if (typeof this.loading !== 'object') {
        this.loading = {};
      }
    this.loading[index] = false;
    this.$emit('plot-removed', index);
    this.updateDashboard();
    eventBus.emit('chart-removed', chart);
  },

  teardownLegendScroll(index) {
    if (this.legendScrollCleanups[index]) {
      try {
        this.legendScrollCleanups[index]();
      } catch {
        void 0;
      }
      delete this.legendScrollCleanups[index];
    }
  },

  setupLegendClickHandler(chartDiv) {
    if (!chartDiv) {
      console.warn('Chart container not found for legend click handler');
      return;
    }

    if (typeof chartDiv.removeAllListeners !== 'function' || typeof chartDiv.on !== 'function') {
      console.warn('Chart container is not a valid Plotly container');
      return;
    }

    try {
      chartDiv.removeAllListeners('plotly_legendclick');
      
      chartDiv.on('plotly_legendclick', (event) => {
        const clickedItem = event.curveNumber;
        const traces = event.data;
        
        if (clickedItem !== undefined && traces && traces[clickedItem]) {
          const clickedTrace = traces[clickedItem];
          
          const updates = {};
          updates[`visible[${clickedItem}]`] = !clickedTrace.visible;
          
          Plotly.restyle(chartDiv, updates);
        }
      });
    } catch (error) {
      console.error('Error setting up legend click handler:', error);
    }
  },
  },

  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
  },
  mounted() {
    eventBus.on("resizeChart", this.resizeAllCharts);
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    Object.keys(this.legendScrollCleanups).forEach((key) => {
      this.teardownLegendScroll(Number(key));
    });
  },
};
</script>

<style scoped>
.drag-drop-zone.is-dragging {
  background-color: #f8fafc;
  border-color: #94a3b8;
}

.tabs-no-test-div {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #000000 !important;
}

.tabs-no-test-div h6 {
  color: #000000 !important;
  font-size: 14px;
  margin: 0;
  text-align: center;
}
</style>